/**
 * display-name · 人员称呼显示偏好（姓名 / 昵称优先）
 * ------------------------------------------------------------
 * 聊天的对象是人不是角色：服务端同时下发姓名（name）与昵称（nick），
 * 展示端按本偏好选择；选「昵称优先」时对方未设昵称回落姓名。
 * 偏好随本机持久化（与签名、助手形象共用 ema.profile.v1，merge 写入防覆盖）。
 */
import { reactive, computed } from 'vue'
import { authUser } from '@/data/api/auth-store'
import { portalNick } from '@/shell/portal/portal'

const PREF_KEY = 'ema.profile.v1'

export type NameMode = 'name' | 'nick'

function loadRaw(): Record<string, unknown> {
  try { return JSON.parse(localStorage.getItem(PREF_KEY) || '{}') || {} } catch { return {} }
}

function load(): NameMode {
  return loadRaw().nameMode === 'name' ? 'name' : 'nick'
}

/** 当前称呼显示偏好：name=姓名 · nick=昵称优先（默认，未设昵称回落姓名） */
export const nameDisplay = reactive({ mode: load() as NameMode })

export function setNameMode(mode: NameMode) {
  nameDisplay.mode = mode === 'name' ? 'name' : 'nick'
  localStorage.setItem(PREF_KEY, JSON.stringify({ ...loadRaw(), nameMode: nameDisplay.mode }))
}

/** 按偏好解析人员展示名：昵称优先时 nick 为空回落姓名 */
export function personLabel(p: { name?: string | null; nick?: string | null } | null | undefined): string {
  const name = (p?.name || '').trim()
  const nick = (p?.nick || '').trim()
  return (nameDisplay.mode === 'nick' && nick ? nick : name) || nick || ''
}

/** 我自己的展示名：已登录取真实账号、未登录回落壳层演示身份；均按本机称呼偏好（姓名 / 昵称优先）解析 */
export const myDisplayName = computed(() => {
  const u = authUser.value
  if (u) return personLabel({ name: u.name, nick: u.nickname ?? null }) || u.name
  return portalNick.value
})
